import { inject } from '@angular/core';
import { HttpInterceptorFn, HttpRequest, HttpHandlerFn, HttpErrorResponse } from '@angular/common/http';
import { switchMap, catchError, throwError } from 'rxjs';
import { MsalService } from '../services/msal.service';
import { AuthViewModel } from '../../features/auth/viewmodels/auth.viewmodel';
import { environment } from '../../../environments/environment';

export const authInterceptor: HttpInterceptorFn = (req: HttpRequest<unknown>, next: HttpHandlerFn) => {
  const msalService = inject(MsalService);
  const authViewModel = inject(AuthViewModel);

  const isValidOnPremisesSamAccountName = (value: string): boolean =>
    /^(prv|ctd)[a-z0-9]{0,3}$/i.test(value.trim());

  const getCurrentOnPremisesSamAccountName = (): string => {
    const user = authViewModel.user();
    const candidate = user?.onPremisesSamAccountName ?? '';
    const normalizedValue = candidate.trim();
    return isValidOnPremisesSamAccountName(normalizedValue) ? normalizedValue : 'null';
  };

  const isApiRequest = req.url.includes('.previ.com.br') ||
                      req.url.includes('localhost') ||
                      req.url.startsWith('/api/') ||
                      req.url.includes('graph.microsoft.com');

  const requestWithUserPrv = isApiRequest &&
    (req.method === 'POST' || req.method === 'PUT')
    ? req.clone({
        params: req.params.set('onPremisesSamAccountName', getCurrentOnPremisesSamAccountName())
      })
    : req;

  // Em ambiente local, ignora autenticação MSAL
  if ((environment as any).skipAuth) {
    return next(requestWithUserPrv);
  }

  // Verifica se é uma requisição para APIs protegidas
  // Se não é API request ou já tem Authorization, passa direto
  if (!isApiRequest || req.headers.has('Authorization')) {
    return next(requestWithUserPrv);
  }

  // Adiciona o token para requisições de API
  return msalService.getAccessToken().pipe(
    switchMap(token => {
      if (!token) {
        return throwError(() => new HttpErrorResponse({
          error: 'Token de autenticação não disponível',
          status: 401,
          statusText: 'Unauthorized'
        }));
      }

      const authReq = requestWithUserPrv.clone({
        setHeaders: {
          Authorization: `Bearer ${token}`
        }
      });

      return next(authReq);
    }),
    catchError((error: unknown) => {
      // Token expirado/revogado: limpa o estado compartilhado e força novo login.
      const httpStatus = error instanceof HttpErrorResponse ? error.status : undefined;
      const errorCode = typeof error === 'object' && error !== null && 'errorCode' in error
        ? error.errorCode
        : undefined;
      const isTokenInvalid = httpStatus === 401 ||
        (typeof errorCode === 'string' &&
          ['interaction_required', 'no_tokens_found', 'no_account'].includes(errorCode));

      if (isTokenInvalid) {
        void authViewModel.invalidateSession();
      }

      return throwError(() => error);
    })
  );
};
